import { v4 } from 'uuid'
import { action, observable, makeObservable, computed } from 'mobx'
import ModalViewModel from 'infra/ModalViewModel'
import InventoryStore from 'admin/InventoryStore'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import { ViewModelList } from 'infra/ViewModelList'
import InventoryItemRequestQueryFilterBuilder, {
  InventoryItemArchivedFilterOptions,
} from 'admin/inventory/InventoryItemRequestQueryFilterBuilder'
import InventoryItem, {
  InventoryItemPricing,
} from 'admin/inventory/InventoryItem'
import ScopeChangeRequest from 'admin/scope-change-requests/ScopeChangeRequest'
import { SearchTextViewModel } from 'infra/SearchTextViewModel'
import { switchMap } from 'rxjs'
import { shareLatest } from 'utils/rx-util'

export default class AddInventoryItemsToEditDialogViewModel extends ModalViewModel {
  /**
   * Search term for item selection
   */
  searchText = new SearchTextViewModel()

  /**
   * Selected scope change inventory items
   */
  @observable
  selectedInventoryItems: EditScopeChangeInventoryItemToAdd[] = []

  /**
   * The scope change request
   */
  @observable scopeChangeRequest: ScopeChangeRequest = ScopeChangeRequest.none

  private readonly inventoryItems

  inventoryScrollProvider = new InfiniteScrollProvider<InventoryItem>({
    limit: 100,
    startFetchTaskAsPending: true,
    fetchItems: (paging) =>
      this.inventoryStore.fetchInventoryItems(
        this.scopeChangeRequest.workspace.id
          ? [this.scopeChangeRequest.workspace.id]
          : [],
        paging.limit ?? 100,
        this.searchText.value,
        paging.after ?? undefined,
        new InventoryItemRequestQueryFilterBuilder()
          .withPriceTypes([InventoryItemPricing.Flat])
          .withArchivedStatus(InventoryItemArchivedFilterOptions.ACTIVE)
          .forJobType(this.scopeChangeRequest.job.jobType)
          .build(),
      ),
  })

  /**
   * Server query pipeline.
   */
  query$ = this.searchText.debouncedValue$.pipe(
    switchMap(() => this.inventoryScrollProvider.fetch()),
    shareLatest(),
  )

  constructor(
    private inventoryStore: InventoryStore,
    private onItemsAdded: (
      itemsToAdd: EditScopeChangeInventoryItemToAdd[],
    ) => void,
  ) {
    super()
    makeObservable(this)

    this.inventoryItems = new ViewModelList({
      models: () => this.inventoryScrollProvider.items.slice(),
      create: (model) =>
        new EditScopeChangeInventoryItemToAdd(
          model.id,
          model.versionId,
          model.name,
          model.description,
          this.selectedInventoryItems
            .map((i) => i.versionId)
            .includes(model.versionId),
          (item) => {
            if (item.selected) {
              this.selectedInventoryItems = [
                item,
                ...this.selectedInventoryItems,
              ]
            } else {
              this.selectedInventoryItems = this.selectedInventoryItems.filter(
                (i) => i.versionId !== item.versionId,
              )
            }
          },
        ),
    })
    this.show$.subscribe(this.reset)
    this.query$.pipe(this.whileShowing()).subscribe()

    this.show$.subscribe(() => this.inventoryItems.activate())
    this.close$.subscribe(() => this.inventoryItems.deactivate())
  }

  @action.bound
  reset() {
    this.searchText.clear()
  }

  open(scopeChangeRequest: ScopeChangeRequest) {
    this.scopeChangeRequest = scopeChangeRequest
    this.selectedInventoryItems = []
    this.inventoryScrollProvider.fetch()
    this.show()
  }

  @computed
  get serviceItems() {
    return this.inventoryItems.items
  }

  @action.bound
  addItems() {
    this.onItemsAdded(this.selectedInventoryItems)
    this.close()
  }
}

export class EditScopeChangeInventoryItemToAdd {
  @observable key = ''

  @observable itemId = ''

  @observable versionId = ''

  @observable name = ''

  @observable description = ''

  @observable selected = false

  constructor(
    itemId: string,
    versionId: string,
    name: string,
    description: string,
    selected: boolean,
    private onSelectedChange: (item: EditScopeChangeInventoryItemToAdd) => void,
  ) {
    this.key = v4()
    this.itemId = itemId
    this.versionId = versionId
    this.name = name
    this.description = description
    this.selected = selected
    makeObservable(this)
  }

  @action.bound
  toggleSelected() {
    this.selected = !this.selected
    this.onSelectedChange(this)
  }
}
